import { useEffect, useMemo, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Label } from "@/shared/components/ui/label";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";
import SecondaryIcon from "../../../../../shared/components/SecondaryIcon";
import Icons from "../../../../../shared/constants/icon.constant";
import { useCryptoDepositAddresses } from "../../../services";
import type { CryptoDepositAddress } from "../../../interfaces/crypto.interface";
import { formatNetworkLabel } from "@/shared/utils/network-label";

const TOKEN_META_MAP: Record<string, { name: string; logo?: string }> = {
  BTC: { name: "Bitcoin", logo: "/assets/icons/bitcoin-icon.png" },
  ETH: { name: "Ethereum", logo: "/assets/icons/eth-icon.png" },
  DOGE: { name: "Dogecoin", logo: "/assets/icons/doge-icon.png" },
  USDT: { name: "Tether", logo: "/assets/icons/usdt-icon.png" },
  USDC: { name: "USD Coin" },
  LTC: { name: "Litecoin" },
  POL: { name: "Polygon" },
  EURC: { name: "Euro Coin" },
};

export type DepositCryptoSelection = {
  tokenId: string;
  tokenSymbol: string;
  tokenDisplayName: string;
  tokenLogo?: string;
  networkKey: string;
  networkLabel: string;
  networkDisplayLabel: string;
  walletAddress?: string;
  walletQrCodeUrl?: string | null;
  minDepositFormatted?: string | null;
  minDepositValue?: number | null;
};

interface DepositFundsCryptoProps {
  onSubmit(selection: DepositCryptoSelection): void;
}

const normalizeKeyPart = (value?: string | null) =>
  value?.trim().toLowerCase() ?? "";

const buildAddressKey = (address: CryptoDepositAddress) =>
  [
    normalizeKeyPart(address.cryptocurrency),
    normalizeKeyPart(address.network),
    normalizeKeyPart(address.network_name),
  ].join("::");

const formatLabelPart = (value?: string | null) => {
  if (!value) {
    return "";
  }

  return formatNetworkLabel(value);
};

const buildNetworkDescriptor = (address: CryptoDepositAddress) => {
  const baseNetwork = formatLabelPart(address.network);
  const networkName = formatLabelPart(address.network_name);

  if (baseNetwork && networkName && baseNetwork !== networkName) {
    return `${baseNetwork} • ${networkName}`;
  }

  return networkName || baseNetwork || "Unknown Network";
};

type TokenOption = {
  value: string;
  symbol: string;
  displayName: string;
  logo?: string;
};

type NetworkOption = {
  value: string;
  label: string;
  descriptor: string;
  address: CryptoDepositAddress;
};

export default function DepositFundsCrypto({
  onSubmit,
}: Readonly<DepositFundsCryptoProps>) {
  const {
    data: addressData,
    isLoading: isLoadingAddresses,
    error: addressesError,
  } = useCryptoDepositAddresses();

  const [selectedTokenId, setSelectedTokenId] = useState("");
  const [selectedNetworkKey, setSelectedNetworkKey] = useState("");

  const addresses = useMemo(() => addressData ?? [], [addressData]);

  const tokenOptions = useMemo<TokenOption[]>(() => {
    if (!addresses.length) {
      return [];
    }

    const uniqueTokens = new Map<string, TokenOption>();

    addresses.forEach((entry) => {
      const symbol = (entry.cryptocurrency ?? "").toUpperCase();
      if (!symbol) {
        return;
      }

      if (uniqueTokens.has(symbol)) {
        return;
      }

      const meta = TOKEN_META_MAP[symbol] ?? { name: symbol };

      uniqueTokens.set(symbol, {
        value: symbol,
        symbol,
        displayName: meta.name,
        logo: meta.logo,
      });
    });

    return Array.from(uniqueTokens.values()).sort((a, b) =>
      a.symbol.localeCompare(b.symbol)
    );
  }, [addresses]);

  const networkOptions = useMemo<NetworkOption[]>(() => {
    if (!selectedTokenId) {
      return [];
    }

    const options: NetworkOption[] = [];
    const seenKeys = new Set<string>();

    addresses.forEach((entry) => {
      const symbol = (entry.cryptocurrency ?? "").toUpperCase();
      if (symbol !== selectedTokenId) {
        return;
      }

      const key = buildAddressKey(entry);

      if (!key || seenKeys.has(key)) {
        return;
      }

      seenKeys.add(key);

      const descriptor = buildNetworkDescriptor(entry);

      options.push({
        value: key,
        label: `${symbol} (${descriptor})`,
        descriptor,
        address: entry,
      });
    });

    return options;
  }, [addresses, selectedTokenId]);

  useEffect(() => {
    if (!tokenOptions.length) {
      if (selectedTokenId !== "") {
        setSelectedTokenId("");
      }
      return;
    }

    const hasSelected = tokenOptions.some(
      (option) => option.value === selectedTokenId
    );

    if (!hasSelected) {
      setSelectedTokenId(tokenOptions[0]?.value ?? "");
    }
  }, [tokenOptions, selectedTokenId]);

  useEffect(() => {
    if (!selectedTokenId || !networkOptions.length) {
      if (selectedNetworkKey !== "") {
        setSelectedNetworkKey("");
      }
      return;
    }

    const hasSelectedNetwork = networkOptions.some(
      (option) => option.value === selectedNetworkKey
    );

    if (!hasSelectedNetwork) {
      setSelectedNetworkKey(networkOptions[0]?.value ?? "");
    }
  }, [networkOptions, selectedNetworkKey, selectedTokenId]);

  const selectedNetworkOption = useMemo(
    () =>
      networkOptions.find((option) => option.value === selectedNetworkKey) ??
      null,
    [networkOptions, selectedNetworkKey]
  );

  const selectedAddress = selectedNetworkOption?.address ?? null;

  const selectedWalletAddress = useMemo(() => {
    const candidate = selectedAddress?.address?.trim();
    return candidate && candidate.length > 0 ? candidate : undefined;
  }, [selectedAddress]);

  const selectedWalletQrCodeUrl = useMemo(() => {
    if (!selectedWalletAddress) {
      return null;
    }

    const params = new URLSearchParams({
      size: "220x220",
      data: selectedWalletAddress,
      margin: "1",
    });

    return `https://api.qrserver.com/v1/create-qr-code/?${params.toString()}`;
  }, [selectedWalletAddress]);

  const { minimumAmountLabel, minimumAmountValue } = useMemo(() => {
    const rawMinDeposit = selectedAddress?.minDeposit;

    if (rawMinDeposit === undefined || rawMinDeposit === null) {
      return {
        minimumAmountLabel: null,
        minimumAmountValue: null,
      };
    }

    const numericValue =
      typeof rawMinDeposit === "number"
        ? rawMinDeposit
        : Number.parseFloat(String(rawMinDeposit));

    if (Number.isFinite(numericValue)) {
      const absoluteValue = Math.abs(numericValue);
      const shouldShowFraction = absoluteValue > 0 && absoluteValue < 1;

      const formatted = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: shouldShowFraction ? 2 : 0,
        maximumFractionDigits: shouldShowFraction ? 8 : 2,
      }).format(numericValue);

      const tokenSymbol = selectedTokenId.toUpperCase();

      return {
        minimumAmountLabel: tokenSymbol
          ? `${formatted} ${tokenSymbol}`
          : formatted,
        minimumAmountValue: numericValue,
      };
    }

    return {
      minimumAmountLabel: String(rawMinDeposit),
      minimumAmountValue: null,
    };
  }, [selectedAddress, selectedTokenId]);

  const walletPlaceholder = isLoadingAddresses
    ? "Loading deposit options..."
    : tokenOptions.length
      ? "Select Token"
      : "No deposit options available";

  const currencyPlaceholder = selectedTokenId
    ? networkOptions.length
      ? "Select Network"
      : "No networks available"
    : "Select a token first";

  const isSubmitDisabled =
    isLoadingAddresses ||
    !!addressesError ||
    !selectedTokenId ||
    (networkOptions.length > 0 && !selectedNetworkKey) ||
    !selectedWalletAddress;

  const handleSubmit = () => {
    if (isSubmitDisabled || !selectedAddress || !selectedNetworkOption) {
      return;
    }

    const tokenSymbol = selectedTokenId.toUpperCase();
    const tokenMeta = TOKEN_META_MAP[tokenSymbol] ?? { name: tokenSymbol };
    const networkKey = selectedAddress.network ?? selectedNetworkKey;
    const networkLabel = selectedNetworkOption.descriptor;
    const networkDisplayLabel = `${tokenSymbol} (${networkLabel})`;

    onSubmit({
      tokenId: tokenSymbol,
      tokenSymbol,
      tokenDisplayName: tokenMeta.name ?? tokenSymbol,
      tokenLogo: tokenMeta.logo,
      networkKey,
      networkLabel,
      networkDisplayLabel,
      walletAddress: selectedWalletAddress,
      walletQrCodeUrl: selectedWalletQrCodeUrl ?? undefined,
      minDepositFormatted: minimumAmountLabel,
      minDepositValue: minimumAmountValue,
    });
  };

  return (
    <div className="w-full space-y-4">
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Select Token</Label>
        <Select
          value={selectedTokenId || undefined}
          onValueChange={setSelectedTokenId}
          disabled={isLoadingAddresses || !tokenOptions.length}>
          <SelectTrigger className="w-full h-16!">
            <SelectValue placeholder={walletPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {tokenOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-3">
                  <Avatar className="size-8">
                    {option.logo ? (
                      <AvatarImage src={option.logo} alt={option.symbol} />
                    ) : null}
                    <AvatarFallback className="bg-secondary text-secondary-foreground text-sm">
                      {option.symbol.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start text-left">
                    <span className="text-sm font-medium">{option.symbol}</span>
                    <span className="text-xs text-muted-foreground">
                      {option.displayName}
                    </span>
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Select Network</Label>
        <Select
          value={selectedNetworkKey || undefined}
          onValueChange={setSelectedNetworkKey}
          disabled={
            !selectedTokenId ||
            !networkOptions.length ||
            Boolean(addressesError)
          }>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={currencyPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {networkOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {addressesError ? (
        <p className="text-xs text-red-400 pt-2">
          {addressesError?.message ?? "Failed to load deposit options."}
        </p>
      ) : null}

      <div className="pt-2">
        <Button
          className="w-full"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}>
          Deposit
        </Button>
      </div>
      <div className="text-xs text-label-dark space-y-4 pt-4">
        <p>
          By Clicking the Deposit button, I agree to the Terms of the{" "}
          <b className="text-white">Customer Agreement</b> and the{" "}
          <b className="text-white">Anti-Money Laundering Policy</b>, and
          confirm that the
          <b className="text-white">
            {" "}
            payment will not be made from a third-party account
          </b>
          .
        </p>
        {/* <div className="border-t border-gray-700 border-dashed"></div> */}
        {/* <div className="flex items-center gap-2 mb-2">
          <SecondaryIcon
            icon={Icons.DepositAmountIcon({ width: 15, height: 15 })}
            isActive
          />
          <p className="text-white">
            Minimum Amount: {minimumAmountLabel ?? "N/A"}
          </p>
        </div> */}
        <div className="border-t border-gray-700 border-dashed"></div>
        <div className="flex items-center gap-2 mb-2">
          <SecondaryIcon
            icon={Icons.TimeIcon({ width: 15, height: 15 })}
            isActive
          />
          <p className="text-white">
            Processing time: Up to 2 hours (may vary)
          </p>
        </div>
        <div className="border-t border-gray-700 border-dashed"></div>
        <div className="flex items-center gap-2 mb-2">
          <SecondaryIcon
            icon={Icons.InfoIcon({ width: 15, height: 15 })}
            isActive
          />
          <a href="#" className="text-white underline">
            Additional info.
          </a>
        </div>
      </div>
    </div>
  );
}
